import { ArrowLeftIcon, ArrowRightIcon } from '@langchain/untitled-ui-icons';

import { FC, useMemo } from 'react';

import { cn } from '@/utils/tailwind';

type TProps = {
  currentIndex: number;
  setCurrentIndex: (index: number) => void;
  isMutating: boolean;
  numPages: number;
  onPageChange?: (index: number) => void;
  maxPagesToShow?: number;
};

const MAX_PAGES_TO_SHOW = 5;

const PaginationTabs: FC<TProps> = ({
  currentIndex,
  setCurrentIndex,
  isMutating,
  numPages,
  onPageChange,
  maxPagesToShow = MAX_PAGES_TO_SHOW,
}) => {
  const pagesToShow = useMemo(() => {
    const pages: string[] = [];

    // Display all pages if they fit within the max limit
    if (numPages <= maxPagesToShow) {
      return Array.from({ length: numPages }).map((_, i) => i.toString());
    }

    const first = 0;
    const last = numPages - 1;
    const current = currentIndex;

    if (current <= 1) {
      // Near the start
      pages.push(first.toString(), '1', '2', '...', last.toString());
    } else if (current >= numPages - 2) {
      const center = last - 2;
      // Near the end
      pages.push(
        first.toString(),
        '...',
        center.toString(),
        (last - 1).toString(),
        last.toString()
      );
    } else {
      // if only one one page in between first and center, show it
      const secondElement =
        current - first === 2 ? (current - 1).toString() : '...';

      // if only one one page in between last and center, show it
      const penUltimateElement =
        last - current === 2 ? (last - 1).toString() : '...';
      // In the middle
      pages.push(
        first.toString(),
        secondElement,
        current.toString(),
        penUltimateElement,
        last.toString()
      );
    }

    return pages;
  }, [currentIndex, numPages, maxPagesToShow]);

  return (
    <div className="flex flex-row rounded-md border border-primary">
      <button
        type="button"
        className="flex h-9 w-9 items-center justify-center border-r border-primary hover:opacity-80 disabled:opacity-50"
        onClick={() => {
          const newIndex = currentIndex - 1;
          setCurrentIndex(newIndex);
          onPageChange?.(newIndex);
        }}
        disabled={currentIndex === 0 || isMutating}
      >
        <ArrowLeftIcon />
      </button>
      {pagesToShow.map((page, index) => (
        <button
          key={index}
          type="button"
          className={cn(
            'h-9 w-9 border-r border-primary hover:opacity-80 disabled:opacity-50',
            parseInt(page, 10) === currentIndex && 'br-brand-tertiary'
          )}
          onClick={() => {
            if (page !== '...') {
              setCurrentIndex(parseInt(page, 10));
              onPageChange?.(parseInt(page, 10));
            }
          }}
          disabled={page === '...'}
        >
          {page === '...' ? '...' : parseInt(page, 10) + 1}
        </button>
      ))}
      <button
        type="button"
        className="flex h-9 w-9 items-center justify-center hover:opacity-80 disabled:opacity-50"
        onClick={() => {
          const newIndex = currentIndex + 1;
          setCurrentIndex(newIndex);
          onPageChange?.(newIndex);
        }}
        disabled={currentIndex === numPages - 1 || isMutating}
      >
        <ArrowRightIcon />
      </button>
    </div>
  );
};

export default PaginationTabs;
